import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { catchError, finalize, tap, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { LocalStorageClass } from '../../helpers/local-storage-helper';
import { LoginRequest, LoginResponse, SetPasswordData } from '../../types/auth/login-data.type';
import {
  GetUserTenantsResponse,
  IUserTenantsAttributes,
  SwitchTenantResponse,
} from '../../types/auth/user-tenants.type';
import { ToastService } from '../shared/toast.service';

const endpoints = {
  login: '/token',
  userTenants: '/user/tenants',
  switchTenant: '/user/switch-tenant',
  logout: '/logout',
  refreshToken: '/refresh-token',
  requestActivationCode: '/password-reset',
  setPassword: '/password-reset/token/{token}',
  registration: '/people/users/registrations',
};

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly toast = inject(ToastService);

  readonly tenants = signal<IUserTenantsAttributes[]>([]);

  switchCurrentUserTenants(tenant: IUserTenantsAttributes) {
    const url = `${environment.baseApiRoute}${endpoints.switchTenant}`;
    return this.http.post<SwitchTenantResponse>(url, {
      tenant: tenant.tenant_identifier,
      invalidatePreviousTokens: false,
    });
  }
  login(data: LoginRequest) {
    const headers: HttpHeaders = new HttpHeaders({
      'Content-type': 'application/json',
    });
    const url = `${environment.baseApiRoute}${endpoints.login}`;
    return this.http.post<LoginResponse>(url, data, { headers });
  }

  getCurrentUserTenants() {
    const url = `${environment.baseApiRoute}${endpoints.userTenants}`;
    return this.http.get<GetUserTenantsResponse>(url);
  }

  getCurrentUserTenantsWithoutJwt(jwt: string) {
    const url = `${environment.baseApiRoute}${endpoints.userTenants}`;
    const headers = new HttpHeaders({
      Authorization: jwt,
      accept: 'application / vnd.api + json',
      excludeJwt: 'true',
    });
    return this.http.get<GetUserTenantsResponse>(url, { headers });
  }

  loginSelectTenant(tenant: IUserTenantsAttributes, jwt: string) {
    const url = `${environment.baseApiRoute}${endpoints.switchTenant}`;
    const headers = new HttpHeaders({
      Authorization: jwt,
      accept: 'application / vnd.api + json',
      excludeJwt: 'true',
    });
    return this.http.post<SwitchTenantResponse>(
      url,
      { tenant: tenant.tenant_identifier, invalidatePreviousTokens: false },
      { headers },
    );
  }

  requestActivationCode(email: string) {
    const url = `${environment.baseApiRoute}${endpoints.requestActivationCode}`;
    return this.http.post<void>(url, { email });
  }

  setPassword(data: SetPasswordData) {
    const url = `${environment.baseApiRoute}${endpoints.setPassword}`.replace(
      '{token}',
      data.token,
    );
    return this.http.post<void>(url, data);
  }

  registerNewAccount(attributes: object) {
    const url = `${environment.baseApiRoute}${endpoints.registration}`;
    const data = { type: 'user-registration', attributes };
    return this.http.post(url, { data }).pipe(
      catchError((error) => {
        const detail = error?.error?.errors?.[0]?.title;
        this.toast.error(detail ?? 'Registration failed');
        return throwError(() => error);
      }),
    );
  }

  logout() {
    const url = `${environment.baseApiRoute}${endpoints.logout}`;
    return this.http.post(url, {}).pipe(
      finalize(() => {
        LocalStorageClass.clearUserData();
        window.location.reload();
      }),
    );
  }

  refreshCurrentToken() {
    const url = `${environment.baseApiRoute}${endpoints.refreshToken}`;
    const refreshToken = LocalStorageClass.getRefreshToken();
    const data = { refreshToken };
    return this.http.post(url, data, { headers: { excludeJwt: 'true' } }).pipe(
      tap((response: any) => {
        LocalStorageClass.setToken(response?.token, response?.refreshToken);
      }),
    );
  }
}
